import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import Highcharts3D from "highcharts/highcharts-3d";
import { motion } from "framer-motion";
import { Container, Row, Col, Badge } from "react-bootstrap";
import { Link } from "react-router-dom";
import { FileText, Home, Building2, Search, Filter, CalendarDays, User, Tag, ShieldCheck } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";

// ✅ Safe init (prevents double init in hot reload)
if (typeof Highcharts3D === "function") {
  Highcharts3D(Highcharts);
}

const UtahLegislatureDashboard = () => {
  const [bills, setBills] = useState([]);
  const [search, setSearch] = useState("");
  const [houseFilter, setHouseFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchBills();
  }, []);

  const fetchBills = async () => {
    try {
      setLoading(true);
      const res = await axios.get("https://utsb.utahdata.org/bdxbackend/api/passed-bills");
      setBills(res.data || []);
    } catch (err) {
      console.error(err);
      setError("Failed to load bills data");
    } finally {
      setLoading(false);
    }
  };

  // 🔎 FILTER LOGIC (SAFE)
  const filtered = useMemo(() => {
    return bills.filter((b) => {
      return (
        (!houseFilter || b?.house === houseFilter) &&
        (b?.title || "")
          .toLowerCase()
          .includes(search.toLowerCase())
      );
    });
  }, [bills, search, houseFilter]);

  // 📊 STATS (MEMOIZED)
  const { total, house, senate } = useMemo(() => {
    const total = bills.length;
    const house = bills.filter((b) => b.house === "House").length;
    const senate = bills.filter((b) => b.house === "Senate").length;
    return { total, house, senate };
  }, [bills]);

  // 📊 3D CHART CONFIG
  const chartOptions = useMemo(() => ({
    chart: {
      type: "pie",
      backgroundColor: "transparent",
      options3d: {
        enabled: true,
        alpha: 45,
      },
    },
    credits: {
      enabled: false,
    },
    title: { text: "" },
    plotOptions: {
      pie: {
        innerSize: 80,
        depth: 45,
        dataLabels: {
          enabled: true,
          style: {
            color: "#000",
            fontWeight: "bold",
          },
        },
      },
    },
    series: [
      {
        name: "Bills",
        data: [
          { name: "House", y: house, color: "#CC0000" },
          { name: "Senate", y: senate, color: "#111827" },
        ],
      },
    ],
  }), [house, senate]);

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{
        color: "#333",
        backgroundColor: "#f8fafc"
      }}
    >
      <Header />

      <div
        className="flex-grow"
        style={{
          background:
            "radial-gradient(1200px 640px at 15% 0%, rgba(153, 27, 27, 0.08), transparent 60%), radial-gradient(900px 560px at 90% 10%, rgba(31, 41, 55, 0.06), transparent 55%), #f8fafc",
        }}
      >
        {/* --- HERO SECTION --- */}
        <div
          style={{
            position: "relative",
            background: `linear-gradient(135deg, rgba(11, 18, 32, 0.85) 0%, rgba(30, 41, 59, 0.96) 100%), url(/img/landing-webp/utah_budget.jpg)`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            color: "white",
            padding: "8rem 0 8rem 0",
            clipPath: "polygon(0 0, 100% 0, 100% 88%, 0 100%)",
            marginBottom: "2rem",
          }}
        >
          {/* soft glow accents */}
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              background:
                "radial-gradient(600px 260px at 20% 30%, rgba(220, 38, 38, 0.3), transparent 60%), radial-gradient(600px 260px at 80% 25%, rgba(156, 163, 175, 0.25), transparent 60%)",
              mixBlendMode: "screen",
            }}
          />

          <Container className="relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Row className="align-items-center text-center">
                <Col lg={10} className="mx-auto">
                  <Badge
                    bg="danger"
                    className="mb-3 px-3 py-2 text-uppercase fw-bold rounded-pill"
                    style={{ fontSize: "0.75rem", letterSpacing: "0.05em" }}
                  >
                    Track Policy & Progress
                  </Badge>
                  <h1
                    className="display-4 fw-bold mb-3 tracking-tight"
                    style={{
                      color: "#ffffff",
                      textShadow: "0 10px 34px rgba(0,0,0,0.55)",
                    }}
                  >
                    Utah Legislature 2026 
                  </h1>
                  <p className="lead opacity-90 mb-4 px-lg-5 fw-light text-white">
                    Monitor the status of bills moving through the Utah State
                    House and Senate, examine bill distribution, and stay
                    informed on critical policy changes.
                  </p>
                  
                  {/* BREADCRUMBS */}
                  <div className="flex justify-center flex-wrap gap-2 text-sm text-white/80 mt-6 font-medium tracking-wide">
                    <Link to="/" className="hover:text-white transition-colors">
                      Home
                    </Link>
                    <span className="opacity-50">/</span>
                    <Link to="/utah-at-a-glance" className="hover:text-white transition-colors">
                      Utah At A Glance
                    </Link>
                    <span className="opacity-50">/</span>
                    <span className="text-white">Utah Legislature</span>
                  </div>
                  
                </Col>
              </Row>
            </motion.div>
          </Container>
        </div>

        <Container
          style={{ marginTop: "2rem", position: "relative", zIndex: 100 }}
          className="pb-12"
        >

      {/* ⚠️ ERROR */}
      {error && (
        <div className="bg-red-100 text-red-700 p-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* ⏳ LOADING */}
      {loading && (
        <div className="text-center text-gray-600 py-10">
          Loading bills data...
        </div>
      )}

      {!loading && (
        <>
          {/* 📊 KPI CARDS */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[{
              label: "Total Bills",
              value: total,
              icon: FileText
            }, {
              label: "House Passed",
              value: house,
              icon: Home
            }, {
              label: "Senate Passed",
              value: senate,
              icon: Building2
            }].map((card, i) => {
              const Icon = card.icon;
              return (
                <motion.div
                  key={i}
                  whileHover={{ scale: 1.05 }}
                  className="bg-white/60 backdrop-blur-lg rounded-2xl p-6 shadow-xl border border-gray-200"
                >
                  <div className="flex justify-between items-start mb-2">
                    <p className="text-gray-500 m-0">{card.label}</p>
                    <div className="p-2 bg-red-50 rounded-lg">
                      <Icon size={24} className="text-red-600" />
                    </div>
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 m-0">
                    {card.value}
                  </h2>
                </motion.div>
              );
            })}
          </div>

          {/* 📊 CHART */}
          <div className="mt-10 bg-white/60 backdrop-blur-lg p-6 rounded-2xl shadow-xl">
            <h2 className="text-xl font-bold mb-4">
              Bills Distribution
            </h2>
            <HighchartsReact
              highcharts={Highcharts}
              options={chartOptions}
            />
          </div>

          {/* 🔎 FILTERS */}
          <div className="mt-10 bg-white/80 backdrop-blur-md p-5 rounded-2xl shadow-lg border border-gray-100 flex flex-col md:flex-row gap-4 items-center transition-all">
            <div className="flex items-center w-full md:w-2/3 bg-white border border-gray-200 rounded-xl shadow-sm focus-within:ring-2 focus-within:ring-red-500 focus-within:border-red-500 transition-all overflow-hidden pl-4 pr-2">
              <Search className="text-gray-400 shrink-0" size={20} />
              <input
                placeholder="Search by bill title, sponsor, or ID..."
                className="w-full py-3 px-3 border-none focus:ring-0 outline-none bg-transparent placeholder-gray-400 text-gray-700 font-medium"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className="flex items-center w-full md:w-1/3 bg-white border border-gray-200 rounded-xl shadow-sm focus-within:ring-2 focus-within:ring-red-500 focus-within:border-red-500 transition-all overflow-hidden pl-4 pr-2 relative">
              <Filter className="text-gray-400 shrink-0" size={20} />
              <select
                className="w-full py-3 px-3 border-none focus:ring-0 outline-none bg-transparent appearance-none text-gray-700 font-medium cursor-pointer"
                value={houseFilter}
                onChange={(e) => setHouseFilter(e.target.value)}
              >
                <option value="">All Origin Houses</option>
                <option value="House">House</option>
                <option value="Senate">Senate</option>
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-4 flex items-center px-1 text-gray-500">
                <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
              </div>
            </div>
          </div>

          {/* 📄 BILL LIST */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">

            {filtered.length === 0 && (
              <div className="col-span-full text-center text-gray-500">
                No bills found
              </div>
            )}

            {filtered.map((bill, i) => (
              <motion.div
                key={i}
                whileHover={{ y: -5 }}
                className="bg-white p-5 rounded-2xl shadow-lg border hover:shadow-2xl transition flex flex-col"
              >
                <div className="flex justify-between items-start mb-2">
                  <span className="text-red-600 font-bold text-lg">
                    {bill?.bill_id}
                  </span>
                  <span className="text-xs font-semibold bg-green-100 text-green-800 px-2 py-1 rounded-full whitespace-nowrap">
                    Passed
                  </span>
                </div>

                <h3 className="font-semibold text-gray-900 mb-3 line-clamp-2" title={bill?.title}>
                  {bill?.title}
                </h3>

                <div className="flex-grow space-y-3 text-sm text-gray-600 mt-2">
                  {bill?.sponsor && (
                    <div className="flex items-center justify-between border-b border-gray-50 pb-2">
                      <span className="font-medium flex items-center gap-2 text-gray-500"><User size={14} /> Sponsor</span>
                      <span className="text-gray-800 font-medium">{bill.sponsor}</span>
                    </div>
                  )}
                  {bill?.house && (
                    <div className="flex items-center justify-between border-b border-gray-50 pb-2">
                      <span className="font-medium flex items-center gap-2 text-gray-500"><Building2 size={14} /> Origin House</span>
                      <span className="text-gray-800 font-medium">{bill.house}</span>
                    </div>
                  )}
                  {bill?.date_passed && (
                    <div className="flex items-center justify-between border-b border-gray-50 pb-2">
                      <span className="font-medium flex items-center gap-2 text-gray-500"><CalendarDays size={14} /> Date Passed</span>
                      <span className="text-gray-800 font-medium">{bill.date_passed}</span>
                    </div>
                  )}
                  {bill?.effective_date && (
                    <div className="flex items-center justify-between border-b border-gray-50 pb-2">
                      <span className="font-medium flex items-center gap-2 text-gray-500"><CalendarDays size={14} /> Effective Date</span>
                      <span className="text-gray-800 font-medium">{bill.effective_date}</span>
                    </div>
                  )}
                  {bill?.governor_action && (
                    <div className="flex items-center justify-between border-b border-gray-50 pb-2">
                      <span className="font-medium flex items-center gap-2 text-gray-500"><ShieldCheck size={14} /> Gov. Action</span>
                      <span className="text-gray-800 font-medium text-right leading-tight max-w-[50%]">{bill.governor_action} {bill?.governor_action_date && <span className="block text-xs text-gray-400">({bill.governor_action_date})</span>}</span>
                    </div>
                  )}
                  {bill?.chapter && (
                    <div className="flex items-center justify-between border-b border-gray-50 pb-2">
                      <span className="font-medium flex items-center gap-2 text-gray-500"><Tag size={14} /> Chapter</span>
                      <span className="text-gray-800 font-medium">{bill.chapter}</span>
                    </div>
                  )}
                </div>

                <div className="mt-4 pt-3 border-t border-gray-100">
                  {bill?.link ? (
                    <a
                      href={bill.link}
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-600 hover:text-blue-800 hover:underline font-medium text-sm inline-flex items-center"
                    >
                      View Details <span className="ml-1">→</span>
                    </a>
                  ) : (
                    <span className="text-gray-400 text-sm">No details link available</span>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </>
      )}

        </Container>
      </div>

      <Footer />
    </div>
  );
};

export default UtahLegislatureDashboard;